import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Edit2, Trash2, Linkedin } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerBody,
} from '@/components/ui/drawer';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CommentSection } from '@/components/Comments/CommentSection';
import { InteractionTimeline } from '@/components/Interactions/InteractionTimeline';
import { CandidateForm } from './CandidateForm';
import { useCandidate, useUpdateCandidate, useDeleteCandidate } from '@/hooks/useCandidates';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/toast';
import { formatBRL, formatROA, isStale, daysSince } from '@/lib/utils';
import type { CandidateProfile, FunnelStatus, UpdateCandidateDTO } from '@/types';

function profileLabel(p: CandidateProfile) {
  return { farmer: 'Farmer', hunter: 'Hunter', private: 'Private', alta_renda: 'Alta Renda' }[p];
}

function statusLabel(s: FunnelStatus) {
  return {
    prospeccao: 'Prospeccao',
    r1: 'R1 - Cafe',
    r2_r3: 'R2/R3 - Entrevista',
    proposta: 'Proposta',
    fechado_ganho: 'Fechado - Ganho',
    fechado_perdido: 'Fechado - Perdido',
  }[s];
}

function scoreVariant(score: number): 'green' | 'yellow' | 'red' {
  if (score >= 70) return 'green';
  if (score >= 40) return 'yellow';
  return 'red';
}

interface Props {
  candidateId: string | null;
  open: boolean;
  onClose: () => void;
}

export function CandidateDrawer({ candidateId, open, onClose }: Props) {
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { user } = useAuth();
  const currentUser = (() => {
    if (!user) return 'Anônimo';
    const raw = user.name || user.email;
    if (!raw) return 'Anônimo';
    // Se for e-mail, transforma "rafael.brandao@..." → "Rafael Brandao"
    const local = raw.includes('@') ? raw.split('@')[0] : raw;
    return local!
      .split('.')
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');
  })();

  const { data: candidate, isLoading } = useCandidate(candidateId);
  const updateCandidate = useUpdateCandidate();
  const deleteCandidate = useDeleteCandidate();
  const { toast } = useToast();

  async function handleUpdate(dto: UpdateCandidateDTO) {
    if (!candidateId) return;
    try {
      await updateCandidate.mutateAsync({ id: candidateId, dto });
      setEditOpen(false);
      toast({ title: 'Card atualizado com sucesso', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao atualizar o card', description: 'Tente novamente.', variant: 'error' });
    }
  }

  function handleDelete() {
    if (!candidateId) return;
    deleteCandidate.mutate(candidateId, {
      onSuccess: () => {
        setConfirmDelete(false);
        onClose();
      },
    });
  }

  const stale = candidate ? isStale(candidate.last_interaction_date) : false;
  const days = candidate?.last_interaction_date
    ? daysSince(candidate.last_interaction_date)
    : null;

  return (
    <>
      <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
        <DrawerContent>
          <DrawerHeader>
            {isLoading || !candidate ? (
              <div className="h-5 w-48 bg-bg-elevated animate-pulse rounded" />
            ) : (
              <>
                <div className="flex items-start justify-between gap-2 pr-6">
                  <div>
                    <DrawerTitle>{candidate.name}</DrawerTitle>
                    <p className="text-sm text-text-secondary mt-0.5">
                      {candidate.current_institution} · {candidate.tenure_years}a
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant={scoreVariant(candidate.score)} className="text-base font-mono px-2 py-1">
                      {candidate.score}
                    </Badge>
                    {candidate.linkedin_url && (
                      <a
                        href={candidate.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Ver LinkedIn"
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md transition hover:bg-bg-elevated"
                        style={{ color: '#0a66c2' }}
                      >
                        <Linkedin className="h-4 w-4" />
                      </a>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditOpen(true)}
                      title="Editar"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setConfirmDelete(true)}
                      title="Excluir"
                      className="text-text-muted hover:text-status-red"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="default">{statusLabel(candidate.funnel_status)}</Badge>
                  <Badge variant="indigo">{profileLabel(candidate.profile)}</Badge>
                  {stale && days !== null && (
                    <Badge variant="red">{days}d sem contato</Badge>
                  )}
                </div>
              </>
            )}
          </DrawerHeader>

          {candidate && (
            <DrawerBody>
              <Tabs defaultValue="details">
                <TabsList className="mb-4">
                  <TabsTrigger value="details">Perfil</TabsTrigger>
                  <TabsTrigger value="timeline">Timeline</TabsTrigger>
                  <TabsTrigger value="comments">Comentarios</TabsTrigger>
                </TabsList>

                <TabsContent value="details">
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'AUM', value: formatBRL(candidate.aum_cents) },
                        { label: 'ROA', value: formatROA(candidate.roa_bps) },
                        {
                          label: 'Custodia Est. 12m',
                          value: formatBRL(candidate.estimated_custody_cents),
                        },
                        { label: 'Origem', value: candidate.origin },
                        {
                          label: 'Onboarding',
                          value: candidate.onboarding_forecast ?? '—',
                        },
                        {
                          label: 'Primeiro Contato',
                          value: candidate.first_contact_date
                            ? format(parseISO(candidate.first_contact_date), 'dd/MM/yyyy', {
                                locale: ptBR,
                              })
                            : '—',
                        },
                        {
                          label: 'Ultima Interacao',
                          value: candidate.last_interaction_date
                            ? format(
                                parseISO(candidate.last_interaction_date),
                                'dd/MM/yyyy',
                                { locale: ptBR },
                              )
                            : '—',
                        },
                        {
                          label: 'Motivo Perda',
                          value: candidate.loss_reason ?? '—',
                        },
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <p className="text-xs text-text-muted">{label}</p>
                          <p className="text-sm text-text-primary font-medium mt-0.5">
                            {value}
                          </p>
                        </div>
                      ))}
                    </div>

                    {candidate.qualitative_score && (
                      <div className="rounded-lg border border-border bg-bg-base p-3">
                        <p className="text-xs text-text-muted mb-3">Avaliacao Qualitativa</p>
                        <div className="flex flex-col gap-2">
                          {[
                            {
                              label: 'Empreendedorismo',
                              value: candidate.qualitative_score.entrepreneurship,
                            },
                            {
                              label: 'Execucao',
                              value: candidate.qualitative_score.execution,
                            },
                            {
                              label: 'Ambicao',
                              value: candidate.qualitative_score.ambition,
                            },
                          ].map(({ label, value }) => (
                            <div key={label} className="flex items-center gap-3">
                              <span className="text-xs text-text-secondary w-32 shrink-0">
                                {label}
                              </span>
                              <div className="flex-1 h-1.5 bg-bg-elevated rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-accent-indigo rounded-full"
                                  style={{ width: `${(value / 5) * 100}%` }}
                                />
                              </div>
                              <span className="text-xs font-mono text-text-primary w-4 text-right">
                                {value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="timeline">
                  <InteractionTimeline
                    candidateId={candidate.id}
                    currentUser={currentUser}
                  />
                </TabsContent>

                <TabsContent value="comments">
                  <CommentSection
                    candidateId={candidate.id}
                    currentUser={currentUser}
                  />
                </TabsContent>
              </Tabs>
            </DrawerBody>
          )}
        </DrawerContent>
      </Drawer>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {candidate ? `Editar — ${candidate.name}` : 'Editar candidato'}
            </DialogTitle>
          </DialogHeader>
          {candidate && (
            <CandidateForm
              candidate={candidate}
              onSubmit={handleUpdate}
              onCancel={() => setEditOpen(false)}
              isLoading={updateCandidate.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmar exclusao</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-text-secondary mb-4">
            Tem certeza que deseja excluir{' '}
            <strong className="text-text-primary">{candidate?.name}</strong>?
            Esta acao nao pode ser desfeita.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteCandidate.isPending}
            >
              {deleteCandidate.isPending ? 'Excluindo...' : 'Excluir'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
